import {
  CustomToolHttpMethod,
  defaultCustomToolTimeoutMs,
  extractCustomToolPathPlaceholders,
  type CustomToolParameter,
  type CustomToolPathParameter,
} from "@repo/app-types/entities/tools/custom";
import {
  KeyValuePairList,
  type KeyValuePair,
} from "@repo/ui/FormLists/keyValuePairList";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { useMemo, type FC } from "react";
import type { FieldView } from "../../../components/diffableField.js";
import type { ToolMockFormSettings } from "../formConfigs/toolMockResponse.js";
import type { BaseToolFormData } from "../hooks/useBaseToolForm.js";
import { CustomToolParameterFormBuilder } from "./customToolParameterFormBuilder.js";
import { EndsCallField } from "./endsCallField.js";
import { SharedToolFormFields } from "./sharedToolFormFields.js";
import { SigningKeyDisplay } from "./signingKeyDisplay.js";
import { ToolMockResponseField } from "./toolMockResponseField.js";

const defaultCustomToolTimeoutMsString = defaultCustomToolTimeoutMs.toString();

export const httpMethodOptions: SimpleSelectOption<CustomToolHttpMethod>[] = [
  { value: CustomToolHttpMethod.GET, label: "GET" },
  { value: CustomToolHttpMethod.POST, label: "POST" },
  { value: CustomToolHttpMethod.PUT, label: "PUT" },
  { value: CustomToolHttpMethod.PATCH, label: "PATCH" },
];

interface CustomToolFormProps {
  name: string;
  description: string;
  settings: CustomToolFormSettings;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSettingsChange: (settings: Partial<CustomToolFormSettings>) => void;
  isEditMode: boolean;
  /** Set while a save is in flight: the editors take no edits. */
  isReadOnly: boolean;
  /** Controls the fields' view. When set, their view toggles don't render. */
  view?: FieldView;
  /** The saved tool, to diff against; `undefined` for a new tool. */
  original: BaseToolFormData<CustomToolFormSettings> | undefined;
}

// Internal settings representation for form state
export interface CustomToolFormSettings extends ToolMockFormSettings {
  method: CustomToolHttpMethod;
  url: string;
  timeout: string;
  timeoutError: string | undefined;
  urlError: string | undefined;
  headers: readonly KeyValuePair[];
  queryParams: readonly KeyValuePair[];
  parameters: readonly CustomToolParameter[];
  pathParameters: readonly CustomToolPathParameter[];
  endsCall: boolean;
}

export const CustomToolForm: FC<CustomToolFormProps> = (props) => {
  const {
    name,
    description,
    settings,
    onNameChange,
    onDescriptionChange,
    onSettingsChange,
    isEditMode,
    isReadOnly,
    view,
    original,
  } = props;
  const pathPlaceholders = useMemo(
    () => extractCustomToolPathPlaceholders(settings.url),
    [settings.url],
  );

  const handlePathParameterChange = (
    name: string,
    description: string,
  ): void => {
    onSettingsChange({
      pathParameters: [
        ...settings.pathParameters.filter(
          (pathParameter) => pathParameter.name !== name,
        ),
        { name, description },
      ],
      urlError: undefined,
    });
  };

  return (
    <>
      {isEditMode && <SigningKeyDisplay />}

      <SharedToolFormFields
        name={name}
        description={description}
        onNameChange={onNameChange}
        originalName={original?.name}
        originalDescription={original?.description}
        onDescriptionChange={onDescriptionChange}
        isReadOnly={isReadOnly}
        view={view}
        namePlaceholder="Fetch customer information"
        descriptionPlaceholder="This tool looks up the customer's plan and account history. The agent should use this if the caller asks about their current plan or billing."
      />

      <div>
        <Label size={Size.SMALL}>API endpoint</Label>
        <div className="mt-2 flex gap-2">
          <div className="w-28">
            <SimpleSelect
              options={httpMethodOptions}
              value={settings.method}
              onChange={(value) => onSettingsChange({ method: value })}
              isLabelHidden
              label="HTTP method"
            />
          </div>
          <div className="flex-1">
            <Input
              id="customUrl"
              value={settings.url}
              onChange={(e) =>
                onSettingsChange({ url: e.target.value, urlError: undefined })
              }
              placeholder="https://api.example.com/endpoint"
              description="Write a path segment as :name to have the agent fill it in, for example https://api.example.com/customers/:customer_id/orders."
              errorDescription={settings.urlError}
              maxLength={2048}
            />
          </div>
        </div>
      </div>

      {pathPlaceholders.length > 0 && (
        <div className="flex flex-col space-y-3">
          <Label size={Size.SMALL}>Path placeholders</Label>
          <p className="text-sm text-muted-foreground">
            Tell the agent what to put in each placeholder in the URL path.
          </p>

          {pathPlaceholders.map((placeholder) => (
            <Input
              key={placeholder}
              id={`customPathParameter-${placeholder}`}
              label={`:${placeholder}`}
              value={
                settings.pathParameters.find(
                  (pathParameter) => pathParameter.name === placeholder,
                )?.description ?? ""
              }
              onChange={(e) =>
                handlePathParameterChange(placeholder, e.target.value)
              }
              placeholder="The customer's account number, as stated by the caller"
              maxLength={2048}
            />
          ))}
        </div>
      )}

      <Input
        id="customTimeout"
        label="Timeout (ms)"
        value={settings.timeout}
        onChange={(e) =>
          onSettingsChange({ timeout: e.target.value, timeoutError: undefined })
        }
        placeholder={defaultCustomToolTimeoutMsString}
        errorDescription={settings.timeoutError}
        maxLength={100}
      />

      <EndsCallField
        isChecked={settings.endsCall}
        onChange={(endsCall) => onSettingsChange({ endsCall })}
      />

      <KeyValuePairList
        label="Headers"
        description="Specify the HTTP headers required for your API request."
        value={settings.headers}
        onChange={(value) => onSettingsChange({ headers: value })}
        addButtonLabel="Add header"
        lengthOptions={{
          keyMinLength: 1,
          keyMaxLength: 512,
          valueMinLength: 1,
          valueMaxLength: 4096,
        }}
      />

      <KeyValuePairList
        label="Query parameters"
        description="Query string parameters to append to the URL."
        value={settings.queryParams}
        onChange={(value) => onSettingsChange({ queryParams: value })}
        addButtonLabel="Add parameter"
        lengthOptions={{
          keyMinLength: 1,
          keyMaxLength: 128,
          valueMinLength: 1,
          valueMaxLength: 1024,
        }}
      />

      <CustomToolParameterFormBuilder
        label="Parameters"
        description="Define the parameters that the agent should provide in the request body to your API endpoint."
        value={settings.parameters}
        onChange={(value) => onSettingsChange({ parameters: value })}
      />

      <ToolMockResponseField
        settings={settings}
        onSettingsChange={onSettingsChange}
        isReadOnly={isReadOnly}
      />
    </>
  );
};
